import React, { useContext, useRef, useLayoutEffect } from "react";
import { ShopContext } from "../context/ShopContext";
import { Link } from "react-router-dom";
import styles from "./ProductCard.module.scss";
import gsap from "gsap";

function ProductCard({ product }) {
  const { addToCart, toggleCartOverlay } = useContext(ShopContext);
  const coverRef = useRef(null);
  const lifestyleRef = useRef(null);
  const isHoveredRef = useRef(false); // tracks hover state without causing re-renders

  const handleAddToCart = () => {
    addToCart(product, 0, 1);
    toggleCartOverlay();
  };

  const coverPhoto = product.variations[0].coverPhoto;
  const fallbackImage = product.variations[0].imageSrc[0];
  const lifestyleImage = product.lifestyleImages?.[0];
  const displayImage = coverPhoto ?? fallbackImage;
  const hasStripeId = product.variations[0].stripeId !== undefined;

  // After every render, immediately restore the correct opacity
  // so a parent re-render never causes a visible flicker
  useLayoutEffect(() => {
    if (!lifestyleImage) return;
    gsap.set(coverRef.current, { opacity: isHoveredRef.current ? 0 : 1 });
    gsap.set(lifestyleRef.current, { opacity: isHoveredRef.current ? 1 : 0 });
  });

  const handleMouseEnter = () => {
    if (!lifestyleImage) return;
    isHoveredRef.current = true;
    gsap.to(coverRef.current, {
      opacity: 0,
      duration: 0.5,
      ease: "power2.inOut",
    });
    gsap.to(lifestyleRef.current, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.inOut",
    });
  };

  const handleMouseLeave = () => {
    if (!lifestyleImage) return;
    isHoveredRef.current = false;
    gsap.to(coverRef.current, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.inOut",
    });
    gsap.to(lifestyleRef.current, {
      opacity: 0,
      duration: 0.5,
      ease: "power2.inOut",
    });
  };

  // Image(s) as plain JSX. IMPORTANT: do NOT wrap these in a component defined
  // inside render — a fresh component identity on each render makes React
  // unmount & remount the <img>s, which re-fetches them (this was the "images
  // reload when scroll direction changes" bug). Inline the wrapper with stable
  // element types (Link / fragment) instead.
  const imageContent = (
    <>
      <img
        ref={coverRef}
        src={displayImage}
        alt={product.name}
        className={`${styles.productImg} ${styles.coverImg}`}
      />
      {lifestyleImage && (
        <img
          ref={lifestyleRef}
          src={lifestyleImage}
          alt={`${product.name} lifestyle`}
          className={`${styles.productImg} ${styles.lifestyleImg}`}
        />
      )}
    </>
  );

  return (
    <div
      className={styles.productCard}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.productImage}>
        {hasStripeId ? (
          <Link to={`/product/${product.id}`}>{imageContent}</Link>
        ) : (
          imageContent
        )}
        {hasStripeId && (
          <Link id={styles.addToCart} onClick={handleAddToCart}>
            Add to Cart
          </Link>
        )}
      </div>
      <div className={styles.productInfo}>
        <div className={styles.productName}>
          {product.name.toUpperCase()}
          {product.size !== "" && (
            <span className={styles.productSize}>
              {product.size.toUpperCase()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Memoized: `product` (and the context fns) are stable references, so the card
// no longer re-renders on every parent re-render (e.g. the scroll-direction
// state change in AllProducts), avoiding wasted work on the whole grid.
export default React.memo(ProductCard);
